import React from "react"
import { useTranslation } from "react-i18next"

interface FooterProps {
    rememberSelection: boolean
    onRememberSelectionChange: (checked: boolean) => void
    onSaveSelection: () => void
    onClearSelection: () => void
    isSaveDisabled: boolean
}

export const Footer: React.FC<FooterProps> = ({
    rememberSelection,
    onRememberSelectionChange,
    onSaveSelection,
    onClearSelection,
    isSaveDisabled
}) => {
    const { t } = useTranslation()
    return (
        <div className="mt-auto p-6">
            <div className="space-y-4">
                {/* Remember selection checkbox */}
                <label className="flex items-center space-x-2 cursor-pointer">
                    <div className="relative">
                        <input
                            type="checkbox"
                            checked={rememberSelection}
                            onChange={(e) => onRememberSelectionChange(e.target.checked)}
                            className="sr-only"
                        />
                        <div className={`w-5 h-5 rounded border flex items-center justify-center transition-colors ${rememberSelection ? 'bg-zinc-800 border-zinc-800 dark:bg-white dark:border-white' : 'bg-transparent border-zinc-500 dark:border-white'}`}>
                            {rememberSelection && (
                                <svg
                                    className="w-4 h-4 text-white dark:text-zinc-900"
                                    fill="currentColor"
                                    viewBox="0 0 20 20"
                                >
                                    <path
                                        fillRule="evenodd"
                                        d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                                        clipRule="evenodd"
                                    />
                                </svg>
                            )}
                        </div>
                    </div>
                    <span className="leading-[20px] text-[14px]">
                        {t('selection.remember')}
                    </span>
                </label>

                {/* Action buttons */}
                <div className="flex space-x-2">
                    <button
                        onClick={onSaveSelection}
                        disabled={isSaveDisabled}
                        className="flex-1 px-4 py-3 text-sm font-medium rounded-full disabled:opacity-50 disabled:cursor-not-allowed transition-colors cursor-pointer bg-zinc-900 hover:bg-zinc-800 text-white dark:bg-white dark:hover:bg-zinc-300 dark:text-zinc-900"
                    >
                        {t('actions.confirm')}
                    </button>
                    <button
                        onClick={onClearSelection}
                        className="truncate flex-1 px-4 py-3 border text-sm font-medium rounded-full transition-colors cursor-pointer bg-transparent border-zinc-200 hover:bg-zinc-100 dark:border-zinc-500 dark:hover:bg-[#1f2347]"
                    >
                        {t('actions.cancel')}
                    </button>
                </div>

                {/* Help text */}
                <div className="text-start">
                    <p className="text-sm">
                        {t('workspace.notFound')}
                    </p>
                    <p className="text-sm">
                        {t('support.visitOur')}{' '}
                        <a
                            target="_blank"
                            rel="noopener noreferrer"
                            className="underline hover:no-underline focus:outline-none cursor-pointer"
                            href={`https://${t('support.url')}`}
                        >
                            {t('support.knowledgeBase')}
                        </a>
                    </p>
                </div>
                {/* Commented out manual language switcher for now. Needs Figma design */}
                {/* <LanguageSwitcher /> */}
            </div>
        </div>
    )
}